import type { BookingFormItem } from '@/stores/bookingFormsStore';

export type ExpressEventCandidate = {
  eventKey: string;
  dealId: number;
  expressBookingId: number | null;
  eventCode: string | null;
  eventDate: string;
  eventTime: string;
  durationMinutes: number;
  startMs: number;
  endMs: number;
};

export type ActiveExpressEvent = ExpressEventCandidate & {
  nowMs: number;
  remainingSeconds: number;
  elapsedMinutes: number;
};

function parseEventDateTime(eventDate: string, eventTime: string): number | null {
  const dateMatch = eventDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return null;

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

  const rawTime = eventTime.trim().toUpperCase();
  const twelveHour = rawTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/);
  const twentyFourHour = rawTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (twelveHour) {
    const baseHours = Number(twelveHour[1]);
    minutes = Number(twelveHour[2]);
    seconds = Number(twelveHour[3] ?? 0);
    const meridiem = twelveHour[4];
    if (baseHours < 1 || baseHours > 12) return null;
    hours = baseHours % 12;
    if (meridiem === 'PM') hours += 12;
  } else if (twentyFourHour) {
    hours = Number(twentyFourHour[1]);
    minutes = Number(twentyFourHour[2]);
    seconds = Number(twentyFourHour[3] ?? 0);
  } else {
    return null;
  }

  if (hours < 0 || hours > 23) return null;
  if (minutes < 0 || minutes > 59) return null;
  if (seconds < 0 || seconds > 59) return null;

  return new Date(year, month - 1, day, hours, minutes, seconds, 0).getTime();
}

function normalizeDurationMinutes(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
}

export function buildExpressEventCandidates(forms: BookingFormItem[]): ExpressEventCandidate[] {
  return forms
    .filter((form) => form.mode === 'express' && form.is_otp_verified === true)
    .map((form) => {
      const dealId = Number(form.deal_id || form.id);
      const eventDate = String(form.event_date ?? '').trim();
      const eventTime = String(form.event_time ?? form.event_start_at ?? '').trim();
      const durationMinutes = normalizeDurationMinutes(
        form.event_duration_mins ?? form.performance_duration_mins
      );
      const startMs = eventDate && eventTime ? parseEventDateTime(eventDate, eventTime) : null;

      if (!Number.isFinite(dealId) || !eventDate || !eventTime || durationMinutes == null || startMs == null) {
        return null;
      }

      const expressBookingIdRaw = Number(form.express_booking_id ?? 0);
      const expressBookingId = Number.isFinite(expressBookingIdRaw) && expressBookingIdRaw > 0
        ? expressBookingIdRaw
        : null;
      const eventCode = typeof form.express_booking_code === 'string' && form.express_booking_code.trim()
        ? form.express_booking_code.trim()
        : null;
      const eventKey = `express:${expressBookingId ?? `deal:${dealId}`}:${eventDate}:${eventTime}`;

      return {
        eventKey,
        dealId,
        expressBookingId,
        eventCode,
        eventDate,
        eventTime,
        durationMinutes,
        startMs,
        endMs: startMs + durationMinutes * 60 * 1000,
      } satisfies ExpressEventCandidate;
    })
    .filter((entry): entry is ExpressEventCandidate => Boolean(entry));
}

export function getActiveExpressEvent(
  candidates: ExpressEventCandidate[],
  nowMs: number = Date.now()
): ActiveExpressEvent | null {
  const active = candidates
    .filter((entry) => nowMs >= entry.startMs && nowMs < entry.endMs)
    .sort((a, b) => b.startMs - a.startMs)[0];

  if (!active) return null;

  const remainingMs = Math.max(0, active.endMs - nowMs);
  const elapsedMs = Math.max(0, nowMs - active.startMs);
  return {
    ...active,
    nowMs,
    remainingSeconds: Math.floor(remainingMs / 1000),
    elapsedMinutes: Math.floor(elapsedMs / 60000),
  };
}

export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

